import type { CSSProperties } from 'react';

/**
 * Posição do cartão explicativo do spotlight da demo em relação ao elemento destacado.
 *
 * - `auto`: tenta acima do alvo; se não couber na viewport, usa abaixo.
 * - `above` / `below`: centro horizontal alinhado ao buraco; vertical fixo.
 * - `inline-start` / `inline-end`: ao lado (em LTR = esquerda / direita), centro vertical no buraco.
 */
export const PRODUCT_DEMO_SPOTLIGHT_PLACEMENTS = [
  'auto',
  'above',
  'below',
  'inline-start',
  'inline-end',
] as const;

export type ProductDemoSpotlightPlacement =
  (typeof PRODUCT_DEMO_SPOTLIGHT_PLACEMENTS)[number];

export type ProductDemoSpotlightHoleRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

export interface ComputeProductDemoSpotlightStyleParams {
  hole: ProductDemoSpotlightHoleRect;
  placement: ProductDemoSpotlightPlacement;
  cardWidthPx: number;
  cardHeightPx: number;
  viewportWidthPx: number;
  viewportHeightPx: number;
  viewportPadPx: number;
  gapPx: number;
}

/**
 * Calcula `style` para um cartão `position: fixed` com âncora coerente com `transform`.
 */
export function computeProductDemoSpotlightStyle(
  p: ComputeProductDemoSpotlightStyleParams
): CSSProperties {
  const {
    hole,
    placement,
    cardWidthPx: cw,
    cardHeightPx: ch,
    viewportWidthPx: vw,
    viewportHeightPx: vh,
    viewportPadPx: pad,
    gapPx: gap,
  } = p;

  const centerX = hole.left + hole.width / 2;
  const centerY = hole.top + hole.height / 2;

  const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

  let resolved: Exclude<ProductDemoSpotlightPlacement, 'auto'>;
  if (placement === 'auto') {
    const aboveTop = hole.top - gap - ch;
    resolved = aboveTop >= pad ? 'above' : 'below';
  } else {
    resolved = placement;
  }

  const widthStyle = `min(32rem, calc(100vw - ${pad * 2}px))`;

  switch (resolved) {
    case 'above': {
      let top = hole.top - gap;
      const left = clamp(centerX, pad + cw / 2, vw - pad - cw / 2);
      top = clamp(top, pad + ch, vh - pad);
      return {
        left,
        top,
        transform: 'translate(-50%, -100%)',
        width: widthStyle,
        maxWidth: '32rem',
        right: 'auto',
        bottom: 'auto',
      };
    }
    case 'below': {
      let top = hole.top + hole.height + gap;
      const left = clamp(centerX, pad + cw / 2, vw - pad - cw / 2);
      top = clamp(top, pad, vh - pad - ch);
      return {
        left,
        top,
        transform: 'translate(-50%, 0)',
        width: widthStyle,
        maxWidth: '32rem',
        right: 'auto',
        bottom: 'auto',
      };
    }
    case 'inline-start': {
      let left = hole.left - gap - cw;
      left = clamp(left, pad, vw - pad - cw);
      let top = centerY;
      top = clamp(top, pad + ch / 2, vh - pad - ch / 2);
      return {
        left,
        top,
        transform: 'translate(0, -50%)',
        width: widthStyle,
        maxWidth: '32rem',
        right: 'auto',
        bottom: 'auto',
      };
    }
    case 'inline-end': {
      let left = hole.left + hole.width + gap;
      left = clamp(left, pad, vw - pad - cw);
      let top = centerY;
      top = clamp(top, pad + ch / 2, vh - pad - ch / 2);
      return {
        left,
        top,
        transform: 'translate(0, -50%)',
        width: widthStyle,
        maxWidth: '32rem',
        right: 'auto',
        bottom: 'auto',
      };
    }
    default: {
      let top = hole.top + hole.height + gap;
      const left = clamp(centerX, pad + cw / 2, vw - pad - cw / 2);
      top = clamp(top, pad, vh - pad - ch);
      return {
        left,
        top,
        transform: 'translate(-50%, 0)',
        width: widthStyle,
        maxWidth: '32rem',
        right: 'auto',
        bottom: 'auto',
      };
    }
  }
}
